import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@shared/formatCurrency';
import { formatCoveragePercentage } from '@/features/shop-insurance';

export interface PremiumLimitNotice {
  label: string;
  explanation: string;
}

interface QuotePriceDisplayProps {
  price: number;
  fundingOption?: string;
  label?: string;
  className?: string;
  coveragePercentage?: number;
  /**
   * Set when the displayed price is not the premium that was requested. Renders a warning
   * beside the amount, because the caveat belongs on the number it qualifies.
   */
  premiumLimit?: PremiumLimitNotice;
}

export function QuotePriceDisplay({
  price,
  fundingOption,
  label = 'Cost',
  className = '',
  coveragePercentage,
  premiumLimit,
}: QuotePriceDisplayProps) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        data-testid="quote-card-cost-display"
        className="text-primary-dark flex items-center gap-2 text-sm font-semibold sm:text-base"
      >
        {formatCurrency(price)}
        {premiumLimit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex cursor-default items-center text-amber-600"
                data-testid="premium-clamped-badge"
                aria-label={premiumLimit.label}
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" align="center" className="max-w-80 whitespace-normal">
              <span className="font-semibold">{premiumLimit.label}.</span>{' '}
              {premiumLimit.explanation}
            </TooltipContent>
          </Tooltip>
        )}
        {coveragePercentage !== undefined && (
          <Badge variant="success">{formatCoveragePercentage(coveragePercentage)}% Coverage</Badge>
        )}
      </div>
      {fundingOption && <div className="text-muted-foreground text-xs italic">{fundingOption}</div>}
    </div>
  );
}
